import { MediaImage } from '@/lib/mediaDisplay';
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

interface MediaLightboxProps {
  /** Stored image URL; protected media resolves to component-owned display bytes. */
  src: string;
  scopeKey?: string;
  alt?: string;
  filename?: string;
  onClose: () => void;
}

const MIN_SCALE = 1; // 1 == fit-to-screen (object-contain)
const MAX_SCALE = 8;

const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

// Keep the scaled image from being panned entirely off-screen. Pure so the
// scratch self-check (scratchpad/clamp-check.mjs) can mirror it 1:1.
// Invariant: at scale 1 (fits container) overflow is 0 -> pan is forced to 0.
function clampPan(tx: number, ty: number, scale: number, fw: number, fh: number, cw: number, ch: number) {
  const ox = Math.max(0, (fw * scale - cw) / 2);
  const oy = Math.max(0, (fh * scale - ch) / 2);
  return { tx: Math.min(ox, Math.max(-ox, tx)), ty: Math.min(oy, Math.max(-oy, ty)) };
}

/**
 * Fullscreen image lightbox with wheel/button zoom and drag pan.
 * Dependency-free: a portal onto document.body + a CSS transform driven by
 * plain React state. Closes on Esc, backdrop click, or the X button.
 */
export function MediaLightbox({ src, scopeKey, alt, filename, onClose }: MediaLightboxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const natural = useRef({ w: 0, h: 0 });
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const wasDrag = useRef(false); // survives pointerup -> click so a pan doesn't close
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });

  // Fitted (object-contain) size of the image plus the container box.
  const fitted = useCallback(() => {
    const c = containerRef.current;
    const { w: nw, h: nh } = natural.current;
    if (!c || !nw || !nh) return { fw: 0, fh: 0, cw: 0, ch: 0 };
    const cw = c.clientWidth;
    const ch = c.clientHeight;
    const s = Math.min(cw / nw, ch / nh);
    return { fw: nw * s, fh: nh * s, cw, ch };
  }, []);

  // Zoom keeping the point (px,py) — measured relative to the container CENTER —
  // stationary under the cursor. computeScale receives the previous scale.
  const zoom = useCallback((computeScale: (prev: number) => number, px: number, py: number) => {
    setView((v) => {
      const s1 = clampScale(computeScale(v.scale));
      const ratio = s1 / v.scale;
      const nx = px - (px - v.x) * ratio;
      const ny = py - (py - v.y) * ratio;
      const { fw, fh, cw, ch } = fitted();
      const p = clampPan(nx, ny, s1, fw, fh, cw, ch);
      return { scale: s1, x: p.tx, y: p.ty };
    });
  }, [fitted]);

  const resetView = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  // Reset when the viewed image changes.
  useEffect(() => {
    natural.current = { w: 0, h: 0 };
    setView({ scale: 1, x: 0, y: 0 });
  }, [src]);

  // Esc to close + lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Native, non-passive wheel listener so preventDefault actually stops the
  // page (React's onWheel is passive and can't).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left - rect.width / 2;
      const py = e.clientY - rect.top - rect.height / 2;
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoom((prev) => prev * factor, px, py);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [zoom]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, moved: false };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    d.x = e.clientX;
    d.y = e.clientY;
    setView((v) => {
      const { fw, fh, cw, ch } = fitted();
      const p = clampPan(v.x + dx, v.y + dy, v.scale, fw, fh, cw, ch);
      return { ...v, x: p.tx, y: p.ty };
    });
  };

  const onPointerUp = () => {
    wasDrag.current = drag.current?.moved ?? false;
    drag.current = null;
  };

  // Backdrop click (no drag) closes; clicks on the image don't reach the container.
  const onBackdropClick = (e: React.MouseEvent) => {
    const dragged = wasDrag.current;
    wasDrag.current = false;
    if (e.target === containerRef.current && !dragged) onClose();
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left - rect.width / 2;
    const py = e.clientY - rect.top - rect.height / 2;
    zoom((prev) => (prev > 1 ? 1 : 2), px, py);
  };

  const ctrl = 'flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={filename ? `Viewing ${filename}` : 'Image viewer'}
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-black/90 select-none transition-opacity duration-(--duration-dialog) ease-out starting:opacity-0"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onBackdropClick}
      onDoubleClick={onDoubleClick}
    >
      {filename && (
        <div className="pointer-events-none absolute left-4 top-4 max-w-[50vw] truncate text-sm text-white/80">
          {filename}
        </div>
      )}

      {/* Controls — stopPropagation so they don't trigger backdrop-close. */}
      <div
        className="absolute right-3 top-3 z-10 flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={ctrl} title="Zoom out" aria-label="Zoom out"
          onClick={() => zoom((p) => p / 1.4, 0, 0)}>
          <ZoomOut className="h-4 w-4" />
        </button>
        <span className="min-w-[3.25rem] text-center text-xs tabular-nums text-white/80">
          {Math.round(view.scale * 100)}%
        </span>
        <button type="button" className={ctrl} title="Zoom in" aria-label="Zoom in"
          onClick={() => zoom((p) => p * 1.4, 0, 0)}>
          <ZoomIn className="h-4 w-4" />
        </button>
        <button type="button" className={ctrl} title="Fit to screen" aria-label="Fit to screen"
          onClick={resetView}>
          <Maximize2 className="h-4 w-4" />
        </button>
        <button type="button" className={ctrl} title="Close (Esc)" aria-label="Close"
          onClick={onClose}>
          <X className="h-4 w-4" />
        </button>
      </div>

      <MediaImage
        scopeKey={scopeKey}
        src={src}
        alt={alt || filename || ''}
        draggable={false}
        onLoad={(e) => { natural.current = { w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }; }}
        className="max-h-full max-w-full object-contain text-transparent"
        style={{
          transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
          transformOrigin: 'center center',
          transition: drag.current ? 'none' : 'transform 0.08s var(--ease-out)',
          cursor: view.scale > 1 ? 'grab' : 'default',
          willChange: 'transform',
        }}
      />
    </div>,
    document.body,
  );
}
